/**
 * Recovery from a stale build.
 *
 * A PWA is a *cached* client, and its cache outlives a deploy. When the shell an
 * installed app is holding points at a hashed chunk that the current deployment
 * no longer contains, the import fails and — with the route views behind
 * `lazy()` (`App.tsx`) — React tears the tree down. The user gets a blank
 * screen, which is the single least diagnosable failure this app can produce.
 *
 * This is engine-agnostic. It was reported on Chrome and reproduced in both
 * Chromium and WebKit against production; only the error string differs.
 *
 * Cloudflare Pages made that worse than it needed to be: `public/_redirects`
 * used to answer *every* unmatched path with `index.html` and a 200, so a dead
 * chunk came back as HTML wearing a JavaScript URL rather than as a 404. That is
 * fixed at the edge now — but a client that is already stale has already cached
 * the shell, and cannot benefit from a fix it has not fetched. So the recovery
 * has to live here too, in the client, where the failure actually surfaces.
 *
 * INV-7: loud, not fatal. The boundary above this names the failure and offers
 * the way out; nothing here is swallowed.
 */

/**
 * Did this error come from a chunk the current deployment no longer serves?
 *
 * Every engine words this differently, and none of them expose a code, so string
 * matching is the only option available. Both wordings below were produced
 * against production on 2026-07-27, from the same stale URL:
 *
 *   WebKit    TypeError: 'text/html' is not a valid JavaScript MIME type.
 *   Chromium  TypeError: Failed to fetch dynamically imported module: …
 *
 * The list is deliberately broad — the cost of a false positive is one extra
 * reload of an app that was already broken enough to reach an error boundary;
 * the cost of a false negative is the blank screen this module exists to end.
 */
export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const text = message.toLowerCase();

  return (
    // WebKit, when Pages handed it index.html for a .js URL.
    text.includes("is not a valid javascript mime type") ||
    // Chromium — which reports the fetch as failed rather than naming the MIME
    // type, whether the response was HTML or an honest 404. This is the one the
    // reported incident actually hit.
    text.includes("failed to fetch dynamically imported module") ||
    text.includes("importing a module script failed") ||
    // Firefox.
    text.includes("error loading dynamically imported module") ||
    // The parse that follows when a document is executed as a script anyway.
    text.includes("unexpected token '<'") ||
    text.includes("expected expression, got '<'")
  );
}

/**
 * One automatic recovery per browsing session, and no more.
 *
 * Without a latch, an error that survives the reload — a genuine bug rather than
 * a stale cache — becomes an infinite reload loop, which is worse than the blank
 * screen. `sessionStorage` scopes the latch to this tab: closing the app and
 * coming back later is allowed to try again, because by then the deployment may
 * genuinely have changed.
 */
const RECOVERY_LATCH = "aegae.recovering";

export function hasAlreadyRecovered(): boolean {
  try {
    return sessionStorage.getItem(RECOVERY_LATCH) !== null;
  } catch {
    // Private mode, or storage disabled. Treat as "already tried" rather than
    // risk the loop.
    return true;
  }
}

function latchRecovery(): void {
  try {
    sessionStorage.setItem(RECOVERY_LATCH, "1");
  } catch (error) {
    // Loud, not fatal (INV-7). An unwritable latch is not swallowed: it is the
    // one condition under which recovery declines to reload at all, because an
    // unguarded reload is an infinite loop. `recoverFromStaleBuild` sees this by
    // re-reading the latch, and the boundary falls through to the visible error.
    console.warn("Recovery latch could not be written; declining to reload.", error);
  }
}

/**
 * Drop every cached artefact of the old build and reload into the new one.
 *
 * Order matters. The service worker is unregistered *first*: it is what answers
 * navigations from the precache, so deleting the caches while it still controls
 * the page would let it re-serve — and in `autoUpdate` mode, re-precache — the
 * same stale shell before the reload lands.
 *
 * Dexie is never touched. The ledger is the one thing in this app that cannot be
 * refetched, and a corrupted *build* says nothing about the user's data.
 */
export async function clearBuildCaches(): Promise<void> {
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  if (typeof caches !== "undefined") {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

/**
 * The whole sequence: latch, clear, reload. Returns only if recovery was
 * declined — the reload does not resolve.
 */
export async function recoverFromStaleBuild(): Promise<void> {
  if (hasAlreadyRecovered()) return;
  latchRecovery();
  if (!hasAlreadyRecovered()) return;

  await clearBuildCaches();
  location.reload();
}
